<template>
  <div :class="$style.container">
    <h1 :class="$style.heading">Documents</h1>
    <div :class="$style['navigation-wrapper']">
      <Dropdown
        v-model="activeCategory"
        :class="$style['navigation-mobile']"
        :options="categories"
        placeholder="Select"
        option-label="name"
        option-value="name"
        append-to="self"
        @change="changeCategory(activeCategory)"
      />
      <ul :class="$style.navigation">
        <li v-for="category in categories" :class="$style['navigation__item']">
          <button
            :class="[
              $style.button,
              category.name === activeCategory && $style['button--active'],
            ]"
            :disabled="category.name === activeCategory"
            :data-cy="`tab-${category.name}`"
            @click="changeCategory(category.name)"
          >
            <span :class="$style.button__icon">
              <span
                class="material-symbols-outlined"
                :class="$style.button__icon__symbol"
                >{{ category.icon }}</span
              >
            </span>
            {{ $t(`documents.list.category.${category.name}`) }}
          </button>
        </li>
      </ul>
    </div>
    <div :class="$style.content">
      <DocumentsRequestedTable
        v-if="hasActiveDocumentRequests"
        :format-type="formatType"
        :is-loading="isLoading"
        @openUploadModal="openUploadModal"
      />

      <div v-if="isContractType" :class="$style.subtitle">
        <span>{{ $t('documents.list.listSignedContracts') }}</span>
      </div>

      <div :class="$style['table-navigation']">
        <DSButton
          v-if="!isContractType"
          label="Upload Document"
          data-cy="upload-button"
          :loading="isLoading"
          @click="openUploadModal()"
        />
        <div :class="$style['table-navigation__right']">
          <InputText
            ref="search"
            v-model="tableFilters.name.constraints[0].value"
            name="search"
            placeholder="Search"
            data-cy="search-input"
          />
          <DSButton
            outlined
            label="Clear Filters"
            class="p-button-outlined"
            icon="pi pi-filter-slash"
            data-cy="clear-filters-button"
            @click="clearFilters()"
          />
        </div>
      </div>

      <DataTable
        v-model:filters="tableFilters"
        v-model:first="currentPage"
        v-model:sort-field="tableSortedBy"
        v-model:editingRows="editingRows"
        filter-display="menu"
        :value="tableData"
        :class="$style.table"
        paginator
        :always-show-paginator="false"
        :rows="20"
        :sort-order="-1"
        responsive-layout="scroll"
        edit-mode="row"
        :loading="isLoading"
        show-gridlines
        @row-edit-save="onRowEditSave"
      >
        <template #empty>
          <div v-if="isLoading">Loading files...</div>
          <div
            v-else-if="activeCategory === DOCUMENT_CATEGORIES.CONTRACTS"
            :class="$style['empty-contracts']"
          >
            <h1 :class="$style['empty-contracts__title']">
              {{ $t('documents.list.emptyContractsListCTA.title') }}
            </h1>
            <p :class="$style['empty-contracts__body']">
              {{ $t('documents.list.emptyContractsListCTA.body') }}
            </p>
            <DSButton
              :class="$style['empty-contracts__button']"
              @click="getEmptyContractsButtonAction()"
              :label="getEmptyContractsButtonLabel()"
            />
          </div>
          <div v-else>No files found.</div>
        </template>

        <template v-if="activeCategory === DOCUMENT_CATEGORIES.ALL">
          <Column field="name" header="Name" :show-filter-operator="false">
            <template #body="slotProps">
              <button
                v-if="canDownload(slotProps.data)"
                text
                :class="$style['file-name']"
                :data-cy="`file ${slotProps.data.name}`"
                @click="downloadFile(slotProps.data)"
              >
                {{ slotProps.data.name }}
              </button>
              <span
                v-else
                text
                :class="$style['file-name-disabled']"
                :data-cy="`file ${slotProps.data.name}`"
              >
                {{ slotProps.data.name }}
              </span>
            </template>

            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            field="type"
            header="Type"
            :show-filter-operator="false"
            :show-add-button="false"
            :show-filter-match-modes="false"
            :filter-menu-class="$style['filter']"
          >
            <template #body="slotProps">
              <span :class="$style.capitalize">
                {{ formatType(slotProps.data.type) }}
              </span>
            </template>
            <template #filter="{ filterModel }">
              <ul>
                <li
                  v-for="(type, index) in getUniqueEntries(tableData, 'type')"
                  :key="`filter-type-${encodeURI(type)}-${index}`"
                >
                  <Checkbox
                    v-model="filterModel.value"
                    :input-id="`filter-type-${encodeURI(type)}-${index}`"
                    :value="type"
                  />
                  <label :for="`filter-type-${encodeURI(type)}-${index}`">
                    {{ formatType(type) }}
                  </label>
                </li>
              </ul>
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            field="createdAt"
            header="Date Uploaded"
            sortable
            data-type="date"
            :show-filter-operator="false"
          >
            <template #body="slotProps">
              {{ formatDate(slotProps.data.createdAt, dateDisplayFormat) }}
            </template>
            <template #filter="{ filterModel }">
              <Calendar
                v-model="filterModel.value"
                name="calendar"
                :manual-input="false"
                placeholder="yyyy-mm-dd"
                show-icon
              />
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>
        </template>

        <template
          v-if="
            [
              DOCUMENT_CATEGORIES.BUSINESS,
              DOCUMENT_CATEGORIES.FINANCIAL,
            ].includes(activeCategory)
          "
        >
          <Column field="name" header="Name" :show-filter-operator="false">
            <template #body="slotProps">
              <button
                text
                :class="$style['file-name']"
                :data-cy="`file ${slotProps.data.name}`"
                @click="downloadFile(slotProps.data)"
              >
                {{ slotProps.data.name }}
              </button>
            </template>

            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            field="type"
            header="Type"
            :show-filter-operator="false"
            :show-add-button="false"
            :show-filter-match-modes="false"
            :filter-menu-class="$style['filter']"
          >
            <template #body="slotProps">
              <span :class="$style.capitalize">
                {{ formatType(slotProps.data.type) }}
              </span>
            </template>
            <template #filter="{ filterModel }">
              <ul>
                <li
                  v-for="(type, index) in getUniqueEntries(tableData, 'type')"
                  :key="`filter-type-${encodeURI(type)}-${index}`"
                >
                  <Checkbox
                    v-model="filterModel.value"
                    :input-id="`filter-type-${encodeURI(type)}-${index}`"
                    :value="type"
                  />
                  <label :for="`filter-type-${encodeURI(type)}-${index}`">
                    {{ formatType(type) }}
                  </label>
                </li>
              </ul>
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
            <template #editor="{ data, field }">
              <Dropdown
                v-model="data[field]"
                :options="typesInCategories[activeCategory]"
                placeholder="Select a Type"
              />
            </template>
          </Column>

          <Column
            field="createdAt"
            header="Date Uploaded"
            sortable
            data-type="date"
            :show-filter-operator="false"
          >
            <template #body="slotProps">
              {{ formatDate(slotProps.data.createdAt, dateDisplayFormat) }}
            </template>
            <template #filter="{ filterModel }">
              <Calendar
                v-model="filterModel.value"
                name="calendar"
                :manual-input="false"
                placeholder="yyyy-mm-dd"
                show-icon
              />
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            :row-editor="true"
            class="edit-column"
            :class="$style['edit-column']"
          ></Column>
        </template>

        <template v-if="activeCategory === DOCUMENT_CATEGORIES.BANK">
          <Column field="name" header="Name" :show-filter-operator="false">
            <template #body="slotProps">
              <button
                text
                :class="$style['file-name']"
                :data-cy="`file ${slotProps.data.name}`"
                @click="downloadFile(slotProps.data)"
              >
                {{ slotProps.data.name }}
              </button>
            </template>

            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            field="type"
            header="Type"
            :show-filter-operator="false"
            :show-add-button="false"
            :show-filter-match-modes="false"
            :filter-menu-class="$style['filter']"
          >
            <template #body="slotProps">
              <span :class="$style.capitalize">
                {{ formatType(slotProps.data.type) }}
              </span>
            </template>

            <template #filter="{ filterModel }">
              <ul>
                <li
                  v-for="(type, index) in getUniqueEntries(tableData, 'type')"
                  :key="`filter-type-${encodeURI(type)}-${index}`"
                >
                  <Checkbox
                    v-model="filterModel.value"
                    :input-id="`filter-type-${encodeURI(type)}-${index}`"
                    :value="type"
                  />
                  <label :for="`filter-type-${encodeURI(type)}-${index}`">
                    {{ formatType(type) }}
                  </label>
                </li>
              </ul>
            </template>

            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>

            <template #editor="{ data, field }">
              <Dropdown
                v-model="data[field]"
                :options="typesInCategories[activeCategory]"
                placeholder="Select a Type"
                :disabled="
                  data?.type === BANK_ACCOUNT_DOC_TYPE?.BANK_STATEMENTS
                "
              />
            </template>
          </Column>

          <Column
            field="metaId"
            header="Bank Account"
            :show-filter-operator="false"
            :show-add-button="false"
            :show-filter-match-modes="false"
            :filter-menu-class="$style['filter']"
          >
            <template #body="slotProps">
              {{ getBankAccountBasedOnId(slotProps.data.metaId) || 'N/A' }}
            </template>
            <template #filter="{ filterModel }">
              <ul>
                <li
                  v-for="(type, index) in getUniqueEntries(tableData, 'metaId')"
                  :key="`filter-type-${encodeURI(type)}-${index}`"
                >
                  <Checkbox
                    v-model="filterModel.value"
                    :input-id="`filter-type-${encodeURI(type)}-${index}`"
                    :value="type"
                  />
                  <label :for="`filter-type-${encodeURI(type)}-${index}`">
                    {{
                      bankAccounts.find((account) => account.id === type)
                        ?.accountNumber || 'N/A'
                    }}
                  </label>
                </li>
              </ul>
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
            <template #editor="{ data, field }">
              <Dropdown
                v-model="data[field]"
                option-label="accountNumber"
                option-value="id"
                :options="bankAccounts"
                placeholder="Select a Bank Account"
                :disabled="
                  data?.type === BANK_ACCOUNT_DOC_TYPE?.BANK_STATEMENTS
                "
              />
            </template>
          </Column>

          <Column
            field="createdAt"
            header="Date Uploaded"
            sortable
            data-type="date"
            :show-filter-operator="false"
          >
            <template #body="slotProps">
              {{ formatDate(slotProps.data.createdAt, dateDisplayFormat) }}
            </template>
            <template #filter="{ filterModel }">
              <Calendar
                v-model="filterModel.value"
                name="calendar"
                :manual-input="false"
                placeholder="yyyy-mm-dd"
                show-icon
              />
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            :row-editor="true"
            class="edit-column"
            :class="$style['edit-column']"
          ></Column>
        </template>

        <template v-if="activeCategory === DOCUMENT_CATEGORIES.CONTRACTS">
          <Column field="name" header="Name" :show-filter-operator="false">
            <template #body="slotProps">
              <button
                text
                :class="$style['file-name']"
                :data-cy="`file ${slotProps.data.name}`"
                @click="downloadFile(slotProps.data)"
              >
                {{ slotProps.data.name }}
              </button>
            </template>

            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            field="type"
            header="Type"
            :show-filter-operator="false"
            :show-add-button="false"
            :show-filter-match-modes="false"
            :filter-menu-class="$style['filter']"
          >
            <template #body="slotProps">
              <span :class="$style.capitalize">
                {{ formatType(slotProps.data.type) }}
              </span>
            </template>
            <template #filter="{ filterModel }">
              <ul>
                <li
                  v-for="(type, index) in getUniqueEntries(tableData, 'type')"
                  :key="`filter-type-${encodeURI(type)}-${index}`"
                >
                  <Checkbox
                    v-model="filterModel.value"
                    :input-id="`filter-type-${encodeURI(type)}-${index}`"
                    :value="type"
                  />
                  <label :for="`filter-type-${encodeURI(type)}-${index}`">
                    {{ formatType(type) }}
                  </label>
                </li>
              </ul>
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>

          <Column
            field="userSignedAt"
            header="Signed On"
            sortable
            data-type="date"
            :show-filter-operator="false"
          >
            <template #body="slotProps">
              <span v-if="slotProps.data.userSignedAt">
                {{ formatDate(slotProps.data.userSignedAt, dateDisplayFormat) }}
              </span>
              <span v-else>-</span>
            </template>
            <template #filter="{ filterModel }">
              <Calendar
                v-model="filterModel.value"
                name="calendar"
                :manual-input="false"
                placeholder="yyyy-mm-dd"
                show-icon
              />
            </template>
            <template #filterapply="{ filterCallback }">
              <DSButton
                label="Apply"
                class="p-button-sm"
                @click="onFilterChange(filterCallback)"
              />
            </template>
          </Column>
        </template>
      </DataTable>
    </div>
    <DocumentsUploadModal
      :open-on-file-type="openOnFileType"
      :visibility="showUploadModal"
      @close-modal="closeUploadModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import DSButton from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { SEGMENT_EVENTS } from '@/data/segment-events';
import analytics from '@/utils/analytics';
import i18n from '@/plugins/i18n';

import {
  BANK_ACCOUNT_DOC_TYPE,
  BUSINESS_DOC_TYPE,
  CONTRACTS_DOC_TYPE,
  ENTITY_TYPE,
} from '@clearbanc/data-common-types';
import {
  DOCUMENT_CATEGORIES,
  FILE_TYPES_IN_CATEGORIES,
} from '@/data/file-upload-types';
import { FilterMatchMode, FilterOperator } from 'primevue/api';
import Checkbox from 'primevue/checkbox';
import Calendar from 'primevue/calendar';
import formatDate from 'date-fns/format';
import { RouteNames } from '@/data/routes';
import DocumentsUploadModal from '@/pages/documents/components/DocumentsUploadModal.vue';
import DocumentsRequestedTable from '@/pages/documents/components/DocumentsRequestedTable.vue';
import { useToast } from '@/composables/useToast';

export default {
  components: {
    DSButton,
    InputText,
    Dropdown,
    DataTable,
    Column,
    Checkbox,
    Calendar,
    DocumentsUploadModal,
    DocumentsRequestedTable,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    const initialTableFilters = {
      name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      category: {
        value: null,
        matchMode: FilterMatchMode.IN,
      },
      type: {
        value: null,
        matchMode: FilterMatchMode.IN,
      },
      createdAt: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      startDate: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      endDate: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      userSignedAt: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      metaId: {
        value: null,
        matchMode: FilterMatchMode.IN,
      },
    };

    return {
      isLoading: false,
      activeCategory: 'all',
      categories: [
        { name: DOCUMENT_CATEGORIES.ALL, icon: 'clear_all' },
        { name: DOCUMENT_CATEGORIES.BUSINESS, icon: 'work' },
        { name: DOCUMENT_CATEGORIES.FINANCIAL, icon: 'attach_money' },
        { name: DOCUMENT_CATEGORIES.BANK, icon: 'home' },
        { name: DOCUMENT_CATEGORIES.CONTRACTS, icon: 'receipt_long' },
      ],
      typesInCategories: FILE_TYPES_IN_CATEGORIES,
      DOCUMENT_CATEGORIES,
      BANK_ACCOUNT_DOC_TYPE,
      initialTableFilters,
      tableFilters: JSON.parse(JSON.stringify(initialTableFilters)),
      currentPage: 0,
      tableSortedBy: 'createdAt',
      dateDisplayFormat: 'MMMM do, yyyy',
      editingRows: [],
      showUploadModal: false,
      openOnFileType: null,
    };
  },
  computed: {
    ...mapGetters([
      'uploads',
      'contracts',
      'currentUpload',
      'bankAccounts',
      'hasActiveDocumentRequests',
      'activeDocumentRequests',
      'isUploadInProgress',
      'userCanSubmitInvoices',
      'user',
    ]),
    ...mapRequestStatuses({
      getUserUploadDetailsRequest: 'GET_USER_UPLOAD_DETAILS',
      getUserContractDownloadRequest: 'GET_DOCUSIGN_CONTRACT_DOWNLOAD',
    }),
    isContractType() {
      return this.activeCategory === DOCUMENT_CATEGORIES.CONTRACTS;
    },
    userUploads() {
      const uploadsWithoutBlankEntities = this.uploads.filter(
        (upload) => upload.entity,
      );
      const updatedUploads = uploadsWithoutBlankEntities.map((upload) => {
        return {
          ...upload,
          type: upload.type || 'Other',
          category: this.getCategory(upload.type),
          createdAt: upload.createdAt
            ? this.convertToDateForFilters(upload.createdAt)
            : undefined,
          endDate: upload.endDate
            ? this.convertToDateForFilters(upload.endDate)
            : undefined,
          startDate: upload.startDate
            ? this.convertToDateForFilters(upload.startDate)
            : undefined,
          metaId: Number(upload.metaId) || 'N/A',
        };
      });

      return this.filterUploadsByCategory(updatedUploads, this.activeCategory);
    },
    userContracts() {
      const updatedContracts = this.contracts
        .filter((contract) => !!contract.fileName && !!contract.userSignedAt)
        .map((contract) => {
          return {
            ...contract,
            name: contract.fileName,
            type: this.getContractType(contract.type),
            category: this.getCategory(contract.type),
            createdAt: contract.createdAt
              ? this.convertToDateForFilters(contract.createdAt)
              : undefined,
            userSignedAt: contract.userSignedAt
              ? this.convertToDateForFilters(contract.userSignedAt)
              : undefined,
          };
        });

      return updatedContracts;
    },
    allUploads() {
      return [...this.uploads, ...this.contracts];
    },
    tableData() {
      if (this.activeCategory === DOCUMENT_CATEGORIES.ALL) {
        return [...this.userUploads, ...this.userContracts];
      }

      const filteredUploads = this.filterUploadsByCategory(
        this.userUploads,
        this.activeCategory,
      );

      if (this.activeCategory === DOCUMENT_CATEGORIES.CONTRACTS) {
        return [...filteredUploads, ...this.userContracts];
      }

      return filteredUploads;
    },
    latestUploadFileType() {
      const latestFile = this.allUploads.reduce((latest, current) => {
        return new Date(latest.createdAt) > new Date(current.createdAt)
          ? latest
          : current;
      }, this.allUploads[0]);
      return latestFile.type;
    },
  },
  methods: {
    formatDate,
    getUniqueEntries(data, key) {
      return [...new Set(data.map((entry) => entry[key]))];
    },
    canDownload(file) {
      return (
        ![ENTITY_TYPE.USER, ENTITY_TYPE.OWNER].includes(file.entity) ||
        file.metaId?.toString() === this.user.id.toString()
      );
    },
    changeCategory(category) {
      this.activeCategory = category;
      this.updateURLParams();
      this.clearFilters();
    },
    updateURLParams() {
      this.$router.push({
        name: this.$route.name,
        query: { category: this.activeCategory },
      });
    },
    checkURLParams() {
      const { category } = this.$route.query ?? {};
      const categoryExistsInOptions = this.categories.some(
        (c) => c.name === category,
      );
      if (categoryExistsInOptions) {
        this.activeCategory = category;
      }
    },
    getCategory(type) {
      const categoryKey = Object.keys(this.typesInCategories).find((category) =>
        this.typesInCategories[category].includes(type),
      );
      return categoryKey ?? 'N/A';
    },
    getContractType(type) {
      return type === CONTRACTS_DOC_TYPE.ACH ? type : 'signed_contract';
    },
    formatType(type) {
      const key = `documents.list.documentType.${type}`;
      if (i18n.te(key)) {
        return i18n.t(key);
      }
      return type?.replace(/_/g, ' ').toLowerCase();
    },
    async downloadUpload(id) {
      await this.$store.dispatchApiAction('GET_USER_UPLOAD_DETAILS', {
        id,
        download: true,
      });

      if (this.getUserUploadDetailsRequest.isSuccess) {
        const { downloadUrl } = this.currentUpload;
        window.open(downloadUrl, '_blank');
      }
    },
    async downloadContract(id) {
      const response = await this.$store.dispatchApiAction(
        'GET_DOCUSIGN_CONTRACT_DOWNLOAD',
        {
          contractId: id,
        },
      );

      if (
        this.getUserContractDownloadRequest.isSuccess &&
        response.downloadURL
      ) {
        window.open(response.downloadURL, '_blank');
      }
    },
    filterUploadsByCategory(uploads, category) {
      if (
        category === DOCUMENT_CATEGORIES.ALL ||
        !this.typesInCategories[category]
      ) {
        return uploads;
      }

      return uploads.filter((upload) =>
        this.typesInCategories[category].includes(upload.type),
      );
    },
    clearFilters() {
      this.currentPage = 0;
      this.tableSortedBy = 'createdAt';
      this.tableFilters = JSON.parse(JSON.stringify(this.initialTableFilters));
    },
    onFilterChange(applyFilters) {
      applyFilters();
    },
    convertToDateForFilters(date) {
      return new Date(date.replace(/-/g, '/').replace(/T.+/, ''));
    },
    async onRowEditSave(editData) {
      this.isLoading = true;

      const { data, newData } = editData;
      const fileChanges = { id: data.id };

      if (data.type === BANK_ACCOUNT_DOC_TYPE.BANK_STATEMENTS) {
        return;
      }

      ['name', 'type', 'startDate', 'endDate', 'metaId'].forEach((field) => {
        if (data[field] !== newData[field]) {
          fileChanges[field] = newData[field];
        }
      });

      if (Object.keys(fileChanges).length > 1) {
        await this.$store.dispatchApiAction('UPDATE_USER_UPLOAD', fileChanges);
        this.getLatestData();
      }
    },
    async getLatestData() {
      this.isLoading = true;
      await this.$store.dispatchApiAction('FETCH_USER_UPLOADS');
      this.isLoading = false;
    },
    downloadFile(data) {
      if (!this.canDownload(data)) {
        return;
      }
      const { id, bucket, category } = data;
      if (DOCUMENT_CATEGORIES.CONTRACTS === category && !bucket) {
        return this.downloadContract(id);
      }

      return this.downloadUpload(id);
    },
    closeUploadModal() {
      this.showUploadModal = false;
    },
    getBankAccountBasedOnId(accountId) {
      return this.bankAccounts.find((account) => account.id === accountId)
        ?.accountNumber;
    },
    openUploadModal(fileType) {
      this.openOnFileType = fileType;
      this.showUploadModal = true;

      analytics.track(SEGMENT_EVENTS.if_upload_document_clicked, {
        location: 'Documents Page',
        fileType: fileType || 'unknown',
      });
    },
    getEmptyContractsButtonAction() {
      if (this.userCanSubmitInvoices) this.$emit('pay-an-invoice');
      else {
        this.$router.push({ name: RouteNames.PROFILE.INDEX });
      }
    },
    getEmptyContractsButtonLabel() {
      return this.$t(
        this.userCanSubmitInvoices
          ? 'documents.list.emptyContractsListCTA.accessWorkingCapital'
          : 'documents.list.emptyContractsListCTA.buttonFinishApplication',
      );
    },
  },
  async mounted() {
    await this.getLatestData();
    this.checkURLParams();
  },
  watch: {
    async allUploads(oldVal, newVal) {
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        if (oldVal.length === newVal.length) {
          return;
        }

        const currentDocumentRequestCount = this.activeDocumentRequests.length;
        await this.$store.dispatchApiAction('GET_DOCUMENT_REQUESTS');
        const updatedDocumentRequestCount = this.activeDocumentRequests.length;

        if (
          this.latestUploadFileType === BUSINESS_DOC_TYPE.FINANCIAL_STATEMENTS
        ) {
          await this.$store.dispatchApiAction('GET_FISCAL_DOCUMENT_CHECKS');
        }

        if (
          currentDocumentRequestCount !== updatedDocumentRequestCount &&
          !updatedDocumentRequestCount
        ) {
          this.isLoading = false;
          this.toast.success('All document requests fulfilled', {
            life: 3000,
          });
          return;
        }

        this.toast.success('File uploaded successfully', {
          life: 3000,
        });
      }

      this.isLoading = false;
    },
    isUploadInProgress(newVal) {
      this.isLoading = newVal;
    },
  },
};
</script>

<style>
.p-datatable .p-paginator {
  padding-bottom: 0;
}

.p-paginator-bottom {
  border: 0;
  padding-bottom: 0;
}

.p-column-filter-menu {
  margin-left: 0;
}

body .p-datatable .p-datatable-tbody > tr > td {
  border-left: inherit;
  border-color: #ceced0;
  padding: 1rem;
}

.p-datatable.p-datatable-gridlines .p-datatable-thead > tr > th {
  border-right: 0;
  background: #f8f8fa;
  padding: 1rem;
  line-height: 1;
}

.p-datatable .p-datatable-tbody > tr > td.edit-column {
  text-align: center;
}

body .p-datatable .p-datatable-tbody > tr:focus {
  outline: none;
}

body .p-component-overlay {
  background-color: rgb(255 255 255 / 50%);
}

.p-datatable .p-datatable-loading-icon {
  color: #7f30ff;
}
</style>

<style module>
.container {
  background: #fff;
  border: 1px solid #ceced0;
  border-radius: 4px;
  max-width: 1100px;
  margin: 0 auto;
}

.heading {
  margin: 0 auto;
  padding: 28px;
}

.navigation-wrapper {
  border-bottom: 2px solid #ceced0;
  padding: 0 28px 28px;
}

.navigation-mobile * {
  text-transform: capitalize;
}

.navigation {
  display: none;
  flex-direction: row;
  justify-content: center;
}

.navigation__item + .navigation__item {
  margin-left: 8px;
}

.button {
  display: flex;
  flex-direction: column;
  min-width: 94px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  line-height: 1;
  padding: 10px;
  text-transform: capitalize;
  font-size: 12px;
  font-weight: bold;
  transition: color 0.2s;
}

.button--active {
  color: #7f30ff;
}

.button__icon {
  width: 45px;
  height: 45px;
  margin: 0 0 10px;
  border-radius: 50%;
  border: 2px solid #aaa;
  display: block;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.2s,
    background 0.2s;
}

.button:hover .button__icon {
  background: #f0e9fb;
}

.button--active .button__icon {
  border-color: #7f30ff;
}

.button--active:hover {
  cursor: default !important;
}

.button--active:hover .button__icon {
  background: none !important;
}

.button__icon .button__icon__symbol {
  font-size: 28px;
}

.button--active .button__icon__symbol {
  color: #111;
}

.button::after {
  content: '';
  display: block;
  width: 100%;
  height: 2px;
  background-color: #7f30ff;
  position: absolute;
  top: calc(100% + 26px);
  left: 0;
  opacity: 0;
  transition: opacity 0.2s;
}

.button--active::after {
  opacity: 1;
}

.content {
  padding: 28px;
}

.subtitle {
  font-size: 1rem;
  font-weight: 600;
  color: #393939;
}

.table-navigation {
  margin-bottom: 28px;
  display: flex;
  flex-direction: column;
}

.table-navigation__right {
  display: grid;
  grid-template-columns: 1fr;
  margin-top: 10px;
  row-gap: 10px;
}

.table {
  font-size: 14px;
}

.capitalize {
  text-transform: capitalize;
}

.file-name {
  word-break: normal;
  text-align: right;
  text-decoration: underline;
  color: #7f30ff;
  font-size: 14px;
}

.file-name-disabled {
  cursor: unset;
  word-break: normal;
  text-align: right;
  color: #7f30ff;
  font-size: 14px;
}

.edit-column {
  white-space: nowrap;
}

@media (min-width: 850px) {
  .heading {
    padding: 48px;
    text-align: center;
  }

  .navigation-wrapper {
    padding: 0 38px 26px;
  }

  .navigation-mobile {
    display: none !important;
  }

  .navigation {
    display: flex;
  }

  .content {
    padding: 48px;
  }

  .table-navigation {
    flex-direction: row;
    align-items: flex-end;
  }

  .table-navigation__right {
    margin-left: auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 16px;
  }

  .file-name {
    text-align: left;
  }
}

.pending-contracts a {
  margin: 0 !important;
}

.filter {
  border: 1px solid #ceced0;

  [class^='p-column-filter-constraint'] {
    border-bottom: 1px solid #ceced0;
  }

  li + li {
    margin-top: 10px;
  }

  div + label {
    margin-left: 10px;
  }

  label {
    cursor: pointer;
    text-transform: capitalize;
  }
}

.empty-contracts {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  width: 100%;
}

.empty-contracts__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #393939;
}

.empty-contracts__body {
  margin-bottom: 1.5rem;
  max-width: 550px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  color: #6c757d;
}
</style>
